import type {
  ColumnPinningState,
  RowData,
  Column,
} from '@tanstack/react-table';
import { isEqual } from 'lodash-es';
import { type JSX } from 'react';
import { type IntlShape } from 'react-intl';

import { MessageContainer } from '../../../../../content/message-container/MessageContainer.js';
import { devLog } from '../../../../../core/utils/logging.js';
import { getColumnLabel } from '../../../../utils/get-column-label.js';
import type {
  DataTableColumnVisibilityState,
  DataTableVisibleColumnLimits,
} from '../../../features/ColumnVisibility/column-visibility-types.js';
import type { TDataTableFeatures } from '../../../features/types/features.types.js';
import { dataTableMessages } from '../../../messages.js';

//#region filtering

/**
 * Generates a map of column IDs to their lowercase labels for efficient filtering.
 *
 * @param columns - The columns to generate labels for.
 * @returns A map of column IDs to lowercase labels.
 * @internal
 */
export function generateColumnLabelsMap<TData extends RowData>(
  columns: Column<TDataTableFeatures, TData>[],
): Map<string, string> {
  const labels = new Map<string, string>();
  for (const col of columns) {
    labels.set(col.id, getColumnLabel(col).toLocaleLowerCase());
    const parent = col.parent;
    if (parent && !labels.has(parent.id)) {
      labels.set(parent.id, getColumnLabel(parent).toLocaleLowerCase());
    }
  }
  return labels;
}

/**
 * Processes a filter string into an array of lowercase words.
 * @param filterValue - The filter string to process.
 * @returns An array of lowercase words.
 * @internal
 */
export function processFilterValue(filterValue: string): string[] {
  return filterValue.toLocaleLowerCase().split(' ').filter(Boolean);
}

//#endregion

//#region feature flag

/**
 * Returns the configuration object form of a programmatic column settings override,
 * or `undefined` when the override is a boolean toggle or not provided.
 * @param override - The override value for a column setting (`columnOrder`, `columnVisibility`,
 *                   or `columnPinning`), as passed to the programmatic `openColumnSettings` call.
 * @param key - The key of the configuration object to return when `override` is an object.
 * @returns the configuration object or undefined.
 *
 * @internal
 */
export function getOverrideValue<T extends object, K extends keyof T>(
  override: boolean | T | undefined,
  key: K,
): T[K] | undefined {
  return typeof override === 'object' ? override[key] : undefined;
}

/**
 * Resolves whether a column settings feature is enabled. An override of `false` disables it.
 * Falls back to the toolbar slot value when no override is provided.
 * @param override - The override value for the feature.
 * @param fallback - The fallback value from the toolbar slot.
 * @returns `true` if the feature is enabled, `false` otherwise.
 *
 * @internal
 */
export function getIsSettingEnabled<T extends object>(
  override: boolean | T | undefined,
  fallback: boolean,
): boolean {
  return override === undefined ? fallback : override !== false;
}

//#endregion

//#region visible-columns-limit

/**
 * Resolves effective warning and error thresholds for `visibleColumnLimits`.
 * Values less than 1 are invalid and treated as unset.
 *
 * @param rawLimit - raw `visibleColumnLimits` value from the consumer; `undefined` when no limits are configured
 * @param mandatoryVisibleCount - number of non-built-in leaf columns with `disableColumnHiding: true`
 * @internal
 */
export function resolveEffectiveLimit(
  rawLimit: DataTableVisibleColumnLimits | undefined,
  mandatoryVisibleCount: number,
): {
  warningLimit: number | undefined;
  errorLimit: number | undefined;
} {
  let warningLimit: number | undefined;
  if (rawLimit?.maxWarning !== undefined) {
    if (rawLimit.maxWarning < 1) {
      devLog(
        'DataTable: visibleColumnLimits.maxWarning must be at least 1. The value has been ignored.',
      );
    } else {
      warningLimit = rawLimit.maxWarning;
    }
  }

  let errorLimit: number | undefined;
  if (rawLimit?.maxError !== undefined) {
    if (rawLimit.maxError < 1) {
      devLog(
        'DataTable: visibleColumnLimits.maxError must be at least 1. The value has been ignored.',
      );
    } else {
      errorLimit = rawLimit.maxError;
    }
  }

  if (
    warningLimit !== undefined &&
    errorLimit !== undefined &&
    warningLimit >= errorLimit
  ) {
    devLog(
      'DataTable: visibleColumnLimits.maxWarning should be less than visibleColumnLimits.maxError. ' +
        'The warning threshold has no effect when it is greater than or equal to the error threshold.',
    );
  }

  if (warningLimit !== undefined && warningLimit <= mandatoryVisibleCount) {
    devLog(
      `DataTable: visibleColumnLimits.maxWarning (${warningLimit}) is at or below the number of non-hideable columns (${mandatoryVisibleCount}). The warning will always be shown.`,
    );
  }

  if (errorLimit !== undefined && errorLimit <= mandatoryVisibleCount) {
    devLog(
      errorLimit < mandatoryVisibleCount
        ? `DataTable: visibleColumnLimits.maxError (${errorLimit}) is below the number of non-hideable columns (${mandatoryVisibleCount}). Apply will always be disabled.`
        : `DataTable: visibleColumnLimits.maxError (${errorLimit}) equals the number of non-hideable columns (${mandatoryVisibleCount}). Apply will only be enabled when all optional columns are hidden.`,
    );
  }

  return { warningLimit, errorLimit };
}

/**
 * Builds the footer feedback message for the column visibility limit feature.
 *
 * Priority: min-error (< 1 visible) → max-error (exceeds hard limit) → max-warning (exceeds soft limit).
 * Returns `undefined` when no condition is active so callers can gate layout on its presence.
 *
 * @param showMinError - Whether the minimum-column error (< 1 visible) is active.
 * @param errorLimit - Resolved hard upper limit; `undefined` when not configured.
 * @param warningLimit - Resolved soft upper limit; `undefined` when not configured.
 * @param visibleColumnCount - Number of currently visible columns in the local (unsaved) state.
 * @param intl - IntlShape instance from the calling component.
 * @internal
 */
export function getVisibilityLimitMessage(
  showMinError: boolean,
  errorLimit: number | undefined,
  warningLimit: number | undefined,
  visibleColumnCount: number,
  intl: IntlShape,
): JSX.Element | undefined {
  let variant: 'critical' | 'warning';
  let description: string;

  if (showMinError && visibleColumnCount < 1) {
    variant = 'critical';
    description = intl.formatMessage(dataTableMessages.visibleColumnsMinError);
  } else if (errorLimit !== undefined && visibleColumnCount > errorLimit) {
    variant = 'critical';
    description = intl.formatMessage(dataTableMessages.visibleColumnsError, {
      limit: errorLimit,
    });
  } else if (warningLimit !== undefined && visibleColumnCount > warningLimit) {
    variant = 'warning';
    description = intl.formatMessage(dataTableMessages.visibleColumnsWarning, {
      limit: warningLimit,
    });
  } else {
    return undefined;
  }

  return (
    <MessageContainer variant={variant}>
      <MessageContainer.Description>{description}</MessageContainer.Description>
    </MessageContainer>
  );
}

//#endregion

//#region general utils

/**
 * Adds the parent column before adjacent leaf columns.
 * @param columns - the leaf column ids.
 * @remarks The same parent column may appear multiple times in the pinned areas.
 */
export function groupColumns<TData extends RowData>(
  columns: Column<TDataTableFeatures, TData>[],
): Column<TDataTableFeatures, TData>[] {
  const groupedColumns: Column<TDataTableFeatures, TData>[] = [];
  let lastParent: Column<TDataTableFeatures, TData> | undefined = undefined;
  for (const col of columns) {
    const parent = col.parent;
    // if the current column has no parent, add it and move on to the next
    if (parent === undefined) {
      lastParent = undefined;
      groupedColumns.push(col);
      continue;
    }
    // if the current column belongs to a new parent, start a new group
    if (parent !== lastParent) {
      groupedColumns.push(parent);
      lastParent = parent;
    }
    groupedColumns.push(col);
  }
  return groupedColumns;
}

//#endregion

//#region visibility

/**
 * @returns true if *at least one* of the child columns can be hidden, false otherwise.
 */
export function isParentColumnHideable<TData extends RowData>(
  childColumns: Column<TDataTableFeatures, TData>[],
): boolean {
  return childColumns.some((c) => c.columnDef.enableHiding);
}

/**
 * Generates an object with the visibility of each column.
 * @param columns - The columns in the table.
 * @param columnVisibility - The visibility of the table columns.
 * @returns an object that contains the column ids as keys with their visibility as values.
 * @internal
 */
export function generateColumnVisibilityObject<TData extends RowData>(
  columns: Column<TDataTableFeatures, TData>[],
  columnVisibility?: Record<string, boolean>,
): Record<string, boolean> {
  return Object.fromEntries(
    columns.map((column) => [
      column.id,
      columnVisibility
        ? (columnVisibility[column.id] ?? true)
        : // Safe cast to align the feature generics
          (
            column as unknown as Column<TDataTableFeatures, TData>
          ).getIsVisible(),
    ]),
  );
}

/**
 * Returns the local column visibility of multiple columns used when clicking show/hide all or parent column toggles.
 * @param columns - The columns in the table.
 * @param currentVisibility - The current column visibility state in the table.
 * @param localColumnsVisibilityState - The current column visibility state in the settings.
 * @internal
 */
export function getMultipleLocalColumnVisibility<TData extends RowData>(
  columns: Column<TDataTableFeatures, TData>[],
  currentVisibility: boolean | 'indeterminate',
  localColumnsVisibilityState?: Record<string, boolean>,
): Record<string, boolean> {
  const newVisibility = currentVisibility !== true;
  const newState = { ...localColumnsVisibilityState };

  for (const column of columns) {
    // Only update visibility if the column can be hidden
    // This prevents both hiding and unhiding columns with disableColumnHiding: true
    if (
      // Safe cast to align the feature generics
      (column as unknown as Column<TDataTableFeatures, TData>).getCanHide()
    ) {
      newState[column.id] = newVisibility;
    }
  }

  return newState;
}

/**
 * Returns the current visibility toggle state based on the column visibility state.
 * @param columnVisibilityState - the current column visibility state.
 * @param column - the column to get the visibility state for.
 */
export function getVisibilityState<TData extends RowData>(
  columnVisibilityState: Record<string, boolean>,
  column: Column<TDataTableFeatures, TData>,
): boolean | 'indeterminate' {
  const childColumns = column.columns;
  // if the column has no child columns, return its visibility state
  if (childColumns.length === 0) {
    return columnVisibilityState?.[column.id] ?? false;
  }
  // if the column is a parent column, we need to consider all child columns to determine its visibility state
  const childrenVisibility = generateColumnVisibilityObject(
    childColumns,
    columnVisibilityState,
  );
  return getCombinedVisibilityState(childrenVisibility, childColumns);
}

/**
 * Gets the combined visibility toggle state for a parent column.
 * @param columnVisibilityState - The visibility of the table columns.
 * @param columns - The columns to check whether visibility can be toggled.
 * @param skipNonHideable - When `true`, columns that can't be hidden
 * are ignored entirely (regardless of their visibility).
 * @returns
 *  - `true` if all columns are visible or disabled,
 *  - `false` if all columns are hidden,
 *  - `indeterminate` otherwise.
 * @internal
 */
export function getCombinedVisibilityState<TData extends RowData>(
  columnVisibilityState: Record<string, boolean>,
  columns: Column<TDataTableFeatures, TData>[],
  skipNonHideable: boolean = false,
): boolean | 'indeterminate' {
  let allVisible = true;
  let allHidden = true;

  for (const column of columns) {
    const isVisible = columnVisibilityState[column.id] !== false;
    // Safe cast to align the feature generics
    const canHide =
      (column as unknown as Column<TDataTableFeatures, TData>).getCanHide() !==
      false;
    // skip non-hideable columns that either can't be toggled at all
    // or are simply not visible
    if (!canHide && (skipNonHideable || !isVisible)) {
      continue;
    }
    if (isVisible) {
      allHidden = false;
    } else {
      allVisible = false;
    }
    // Early exit: mixed state detected.
    if (!allVisible && !allHidden) {
      return 'indeterminate';
    }
  }

  if (allVisible) {
    return true;
  }
  if (allHidden) {
    return false;
  }
  return 'indeterminate';
}

/**
 * Determines whether the visibility of one or multiple columns has changed.
 * @param columnVisibilityToReset - The column visibility state that is used to reset.
 * @param localColumnVisibility - The current column visibility state in the settings.
 * @returns `true` if the visibility has changed for at least one column, `false` otherwise.
 * @internal
 */
export function getHasVisibilityChanged(
  columnVisibilityToReset: DataTableColumnVisibilityState | undefined,
  localColumnVisibility: Record<string, boolean>,
): boolean {
  if (!columnVisibilityToReset) {
    return false;
  }

  return Object.keys(localColumnVisibility).some((key) => {
    // If the key is not present in columnVisibilityToReset, consider it as visible
    if (!(key in columnVisibilityToReset)) {
      return localColumnVisibility[key] !== true;
    }
    return localColumnVisibility[key] !== columnVisibilityToReset[key];
  });
}
//#endregion

//#region ordering
/**
 * Generates a column order that includes parent ids.
 * @param getColumn - the function to get a column by its id.
 * @param columnOrder - the column order with leaf columns only.
 * @returns the column order including parent ids and a list of unique parent ids.
 */
export function generateColumnOrderWithParentIds<TData extends RowData>(
  getColumn: (
    columnId: string,
  ) => Column<TDataTableFeatures, TData> | undefined,
  columnOrder: string[] = [],
): { columnOrderWithParents: string[]; uniqueParentIds: string[] } {
  const columnOrderWithParents: string[] = [];
  const uniqueParentIds = new Set<string>();

  for (const columnId of columnOrder) {
    const column = getColumn(columnId);
    const parentId = column?.parent?.id;

    // insert parent id before child if not already added
    if (parentId && !uniqueParentIds.has(parentId)) {
      columnOrderWithParents.push(parentId);
      uniqueParentIds.add(parentId);
    }
    columnOrderWithParents.push(columnId);
  }
  return {
    columnOrderWithParents,
    uniqueParentIds: Array.from(uniqueParentIds),
  };
}

/**
 * Determines whether the order of one or multiple columns has changed.
 * @param columnOrderToReset - The column order state that is used to reset.
 * @param localColumnOrder - The current column order state in the settings.
 * @returns `true` if the order has changed for at least one column, `false` otherwise.
 * @internal
 */
export function getHasColumnOrderChanged(
  columnOrderToReset: string[] | undefined,
  localColumnOrder: string[],
): boolean {
  if (!columnOrderToReset) {
    return false;
  }
  return !isEqual(columnOrderToReset, localColumnOrder);
}

//#endregion

//#region pinning

/**
 * Determines whether the pinned state of one or multiple columns has changed.
 * @param columnPinnedToReset - The column pinning state that is used to reset.
 * @param localColumnsPinned - The current column pinning state in the settings.
 * @returns `true` if the pinned state has changed for at least one column, `false` otherwise.
 * @internal
 */
export function getHasColumnPinningChanged(
  columnPinnedToReset: ColumnPinningState | undefined,
  localColumnsPinned: ColumnPinningState,
): boolean {
  if (!columnPinnedToReset) {
    return false;
  }
  return (
    !isEqual(columnPinnedToReset.start, localColumnsPinned.start) ||
    !isEqual(columnPinnedToReset.end, localColumnsPinned.end)
  );
}

//#endregion
